import { useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import type { PurchaseOrderSearchInput } from '@repo/api-contract';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import CloseIcon from '@mui/icons-material/Close';
import {
  closeModal,
  searchPurchaseOrders,
} from '../../../../state/purchaseOrderSearchModalSlice';
import DraggablePaper from '../../DraggablePaper';
import Table from './Table';
import { initialPageSize } from './constants';

export default function PurchaseOrderSearchModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tMP } = useTranslation([], {
    keyPrefix: 'team.Modals.PurchaseOrdersSearch',
  });

  const {
    selectedPurchaseOrders,
    multiSelect,
    openPurchaseOrderSearchModal,
    loading,
    onImport,
    onCancel,
  } = useSelector(
    ({ purchaseOrderSearchModalSlice }) => purchaseOrderSearchModalSlice,
  );
  const { team } = useSelector(({ userSlice }) => userSlice);

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeModal());
    if (onCancel) onCancel();
  };
  const importPurchaseOrders = () => {
    dispatch(closeModal());
    if (onImport) onImport(selectedPurchaseOrders);
  };

  const initialResults = async () => {
    const payload: PurchaseOrderSearchInput = {
      query: '',
      searchBy: 'vendorName',
      size: initialPageSize,
      page: 0,
    };
    await dispatch(searchPurchaseOrders(payload));
  };

  useEffect(() => {
    if (!openPurchaseOrderSearchModal || team === 'init' || team === 'loading')
      return;
    const fetchData = async () => {
      await initialResults();
    };
    fetchData();
  }, [openPurchaseOrderSearchModal, team]);

  return (
    <Dialog
      open={openPurchaseOrderSearchModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {tMP('Browse Purchase Order')}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height={800}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={{ height: 800, width: '100%' }}>
            <Table multiSelect={multiSelect} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            sx={{ width: buttonWidth }}
            onClick={importPurchaseOrders}
          >
            {loading ? <CircularProgress size={20} /> : tGlobal('Import')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
